import { http } from "./http";
import { accessTokenSubject, refreshAccessToken, setAccessToken } from "../api";
import type { User } from "../types/User";
import type { PayoutBatch, PayoutOrder, PayoutOrdersPage } from "../types/payouts";
import type { VisitCounts } from "../types/orders";
import type { DjSearchHit, ReferralCodeLookup } from "../types/User";
import type { WalletPassColor } from "../utils/walletPassRenderer";
import type { ReferralAddResponse, ReferralCashout } from "../types/referrals";

// The talent's profile and its money history.
export interface ReferredTalent {
  name: string;
  joinedAt: string;
  requests: number;
  earnedByCurrency: Record<string, number>;
}
export interface ReferralStats {
  eligible: boolean;
  code: string;
  link: string;
  currency: string;
  cap: number;
  joined: number;
  earnedByCurrency: Record<string, number>;
  people: ReferredTalent[];
  /** "Add to my balance" (types/referrals). Absent = the feature is off: no button, today's sheet. */
  cashout?: ReferralCashout;
}

// GET /users/:slug never answers 401: read with a token the server no longer
// takes, the talent's OWN profile comes back as the fan page (no email, no
// practice cards, no earnings). api.ts refreshes a token before it expires;
// this is the net for when that missed (clock off, a refresh lost offline, a
// token that ran out in flight): refresh once, read once more (2026-10-10).
const ownProfileAsFan = (p: Partial<User> | null) =>
  !!p && typeof p === "object" && !("email" in p) && !!p._id && p._id === accessTokenSubject();

async function getProfile(slug: string): Promise<User> {
  const profile = await http.get<User>(`/users/${slug}`);
  if (!ownProfileAsFan(profile)) return profile;
  try {
    setAccessToken(await refreshAccessToken());
  } catch {
    return profile; // no session to refresh: that answer stands
  }
  return http.get<User>(`/users/${slug}`);
}

export const usersApi = {
  get: getProfile,
  bank: (slug: string) => http.get<{ bankLast4: string | null; bankBrand: string | null }>(`/users/${slug}/bank`),
  payouts: (slug: string) => http.get<PayoutBatch[]>(`/users/${slug}/payouts`),
  /** The orders inside one payout, paged. */
  payoutOrdersPage: (slug: string, batchId: string, paging: { page: number; limit: number }) =>
    http.get<PayoutOrdersPage>(`/users/${slug}/payouts/${batchId}/orders`, { params: paging }),
  /** The bare form — an older server answers with a plain array, a newer one with the first page. */
  payoutOrders: (slug: string, batchId: string) =>
    http.get<PayoutOrdersPage | PayoutOrder[]>(`/users/${slug}/payouts/${batchId}/orders`),
  setPrice: (slug: string, price: number) => http.patch<unknown>(`/users/${slug}/price`, { price }),
  /** One answer on a DEMO practice card (1 = September, 2 = the second card) — see lib/demoRequest. */
  advanceDemo: (slug: string, which: 1 | 2, status: "queued" | "played" | "declined") =>
    http.patch<unknown>(`/users/${slug}/demo-request`, { which, status }),
  /** Refer & Earn: code, counts, earnings and who joined. `eligible:false` = not offered in their country. */
  referrals: () => http.get<ReferralStats>("/users/me/referrals"),
  /**
   * Refer & Earn money → the Balance (one Stripe transfer, server
   * lib/referralCredits.js). One `requestKey` per tap, the same key on a retry
   * of that tap (the server answers it from the first try's rows). A 202
   * pending_check resolves here too: read `added` / `reason`, not the status.
   */
  addReferralToBalance: (requestKey: string, currency?: string) =>
    http.post<ReferralAddResponse>("/users/me/referrals/add-to-balance", currency ? { requestKey, currency } : { requestKey }),
  setReferralCode: (code: string) => http.patch<{ code: string }>("/users/me/referral-code", { code }),
  /** Public: who a referral code belongs to (the "Invited by" chip), or { offer: true } for a launch-offer code. 404 = no such code. */
  referrer: (code: string) => http.get<ReferralCodeLookup>(`/users/referral/${encodeURIComponent(code)}`),
  /** Link Unique Visitors for the signed-in talent. */
  linkVisits: () => http.get<VisitCounts>("/users/me/link-visits"),
  list: () => http.get<User[]>("/users"),
  search: (q: string) => http.get<DjSearchHit[]>("/users/search", { params: { q } }),
  /** Profile fields (and the avatar) as multipart; the answer carries only the fields that changed. */
  update: (slug: string, form: FormData) => http.put<Partial<User>>(`/users/${slug}`, form),
  /** Switching currency may reset the price to that currency's placeholder — the answer says so. */
  setCurrency: (slug: string, currency: string) => http.patch<{ pricePerRequest?: number }>(`/users/${slug}/currency`, { currency }),
  setWalletPass: (slug: string, pass: { color: WalletPassColor; customColor: string | null }) => http.patch<unknown>(`/users/${slug}/wallet-pass`, pass),
  /** Schedules the account for deletion in 7 days; signing in again cancels it. */
  requestDeletion: (slug: string) => http.post<{ deletionScheduledFor?: string | null }>(`/users/${slug}/delete`),
  cancelDeletion: (slug: string) => http.post<unknown>(`/users/${slug}/cancel-deletion`),
};
